// Sound effects using Web Audio API with global mute support

const SOUND_MUTED_KEY = 'lemonade_sound_muted';

// Get/set global mute state
export const isSoundMuted = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(SOUND_MUTED_KEY) === 'true';
};

export const setSoundMuted = (muted) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SOUND_MUTED_KEY, muted ? 'true' : 'false');
};

export const toggleSoundMuted = () => {
  const newValue = !isSoundMuted();
  setSoundMuted(newValue);
  return newValue;
};

// Audio context (lazily initialized)
let audioContext = null;

const getAudioContext = () => {
  if (typeof window === 'undefined') return null;
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Resume if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
};

// Check if sound should play
const shouldPlaySound = () => {
  if (typeof window === 'undefined') return false;
  if (isSoundMuted()) return false;
  return true;
};

export const playCompletionSound = () => {
  if (!shouldPlaySound()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  
  // Gentle ascending notes
  oscillator.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
  oscillator.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1); // E5
  oscillator.frequency.setValueAtTime(783.99, ctx.currentTime + 0.2); // G5
  
  gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
  
  oscillator.type = 'sine';
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + 0.5);
};

export const playBadgeSound = () => {
  if (!shouldPlaySound()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  
  // Celebratory ascending arpeggio
  oscillator.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
  oscillator.frequency.setValueAtTime(659.25, ctx.currentTime + 0.08); // E5
  oscillator.frequency.setValueAtTime(783.99, ctx.currentTime + 0.16); // G5
  oscillator.frequency.setValueAtTime(1046.50, ctx.currentTime + 0.24); // C6
  
  gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
  
  oscillator.type = 'sine';
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + 0.6);
};

export const playSubmitSound = () => {
  if (!shouldPlaySound()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  
  // Simple positive beep
  oscillator.frequency.setValueAtTime(659.25, ctx.currentTime); // E5
  
  gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
  
  oscillator.type = 'sine';
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + 0.2);
};

// Session Complete celebration sound - 1.5 second jingle
export const playSessionCompleteSound = () => {
  if (!shouldPlaySound()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  
  // Create multiple oscillators for a richer sound
  const playNote = (freq, startTime, duration, type = 'sine') => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(freq, ctx.currentTime + startTime);
    osc.type = type;
    gain.gain.setValueAtTime(0.15, ctx.currentTime + startTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + startTime + duration);
    osc.start(ctx.currentTime + startTime);
    osc.stop(ctx.currentTime + startTime + duration);
  };
  
  // Celebratory fanfare melody (C major ascending with harmony)
  // First chord: C major
  playNote(523.25, 0, 0.3);      // C5
  playNote(659.25, 0, 0.3);      // E5
  playNote(783.99, 0, 0.3);      // G5
  
  // Rising sequence
  playNote(587.33, 0.25, 0.2);   // D5
  playNote(659.25, 0.4, 0.2);    // E5
  playNote(698.46, 0.55, 0.2);   // F5
  playNote(783.99, 0.7, 0.3);    // G5
  
  // Final triumphant chord: C major (octave higher)
  playNote(1046.50, 0.95, 0.5);  // C6
  playNote(1318.51, 0.95, 0.5);  // E6
  playNote(1567.98, 0.95, 0.5);  // G6
  
  // Sparkle effect at the end
  playNote(2093.00, 1.2, 0.3, 'triangle');  // C7 (soft sparkle)
};

// Program Complete celebration sound - longer, more grand
export const playProgramCompleteSound = () => {
  if (!shouldPlaySound()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  
  const playNote = (freq, startTime, duration, type = 'sine', volume = 0.12) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(freq, ctx.currentTime + startTime);
    osc.type = type;
    gain.gain.setValueAtTime(volume, ctx.currentTime + startTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + startTime + duration);
    osc.start(ctx.currentTime + startTime);
    osc.stop(ctx.currentTime + startTime + duration);
  };
  
  // Grand fanfare for program completion (2 seconds)
  // Opening chord
  playNote(392.00, 0, 0.4);      // G4
  playNote(493.88, 0, 0.4);      // B4
  playNote(587.33, 0, 0.4);      // D5
  
  // Rising fanfare
  playNote(523.25, 0.35, 0.25);  // C5
  playNote(587.33, 0.55, 0.25);  // D5
  playNote(659.25, 0.75, 0.25);  // E5
  playNote(783.99, 0.95, 0.4);   // G5
  
  // Final triumphant chord
  playNote(1046.50, 1.3, 0.7);   // C6
  playNote(1318.51, 1.3, 0.7);   // E6
  playNote(1567.98, 1.3, 0.7);   // G6
  playNote(2093.00, 1.3, 0.7);   // C7
  
  // Sparkle finish
  playNote(2637.02, 1.7, 0.3, 'triangle', 0.08);  // E7
};
